package org.example.newportfolio.components.sections.experience

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.layout.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.newportfolio.models.Experience
import org.example.newportfolio.theme.brand
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList

@Composable
fun ExperienceSection() {
    var activeExperience by remember { mutableStateOf(Experience.First) }
    var dotTopPx by remember { mutableStateOf(0) }
    val visibleStates = remember { mutableStateListOf<Boolean>().apply { repeat(Experience.entries.size) { add(false) } } }
    val palette = ColorMode.current.toPalette()
    val brandColor = palette.brand.primary

    val bp = rememberBreakpoint()
    val sidePaddingValue = when {
        bp >= Breakpoint.XL -> "134px"
        bp >= Breakpoint.LG -> "94px"
        bp >= Breakpoint.MD -> "48px"
        bp >= Breakpoint.SM -> "28px"
        else -> "16px"
    }
    Box(
        modifier = Modifier
            .id("experience")
            .classNames("section-container")
            .fillMaxWidth()
            .styleModifier {
                property("padding-top", "100px")
                property("padding-bottom", "100px")
                property("padding-left", sidePaddingValue)
                property("padding-right", sidePaddingValue)
                // Override kobweb Box's display: grid so absolutely positioned
                // children resolve `left: 50%` against the full padding box
                // (not the inner grid track), keeping the timeline centered.
                property("display", "block")
            }
            .position(Position.Relative),
        contentAlignment = Alignment.Center
    ) {
        // ── Vertical timeline ──────────────────────────────────────
        // Centered between left/right cards via left: 50% + translateX(-50%).
        // Subtle gradient + brand-tinted edges so the line reads as a guide,
        // not a hard border.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .top(80.px)
                .bottom(80.px)
                .left(50.percent)
                .width(2.px)
                .styleModifier {
                    property("transform", "translateX(-50%)")
                    property(
                        "background-image",
                        "linear-gradient(to bottom, " +
                            "rgba(255,255,255,0) 0%, " +
                            "${brandColor.toRgb().copyf(alpha = 0.35f)} 8%, " +
                            "${brandColor.toRgb().copyf(alpha = 0.55f)} 50%, " +
                            "${brandColor.toRgb().copyf(alpha = 0.35f)} 92%, " +
                            "rgba(255,255,255,0) 100%)"
                    )
                    property("border-radius", "2px")
                }
                .displayIfAtLeast(Breakpoint.LG)
        )

        // ── Moving dot tracking the active experience ───────────────
        // Position computed from the actual DOM offset of the active card —
        // hardcoded "ordinal * 130" math doesn't survive variable card heights.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .top(dotTopPx.px)
                .left(50.percent)
                .size(18.px)
                .borderRadius(50.percent)
                .backgroundColor(brandColor)
                .border(3.px, LineStyle.Solid, Colors.White)
                .classNames("experience-timeline-dot")
                .styleModifier {
                    property("transform", "translateX(-50%) translateY(-50%)")
                    property(
                        "box-shadow",
                        "0 0 0 6px ${brandColor.toRgb().copyf(alpha = 0.18f)}, " +
                            "0 0 16px ${brandColor.toRgb().copyf(alpha = 0.55f)}"
                    )
                    property("z-index", "2")
                }
                .transition(Transition.of("top", 0.45.s, TransitionTimingFunction.EaseInOut))
                .displayIfAtLeast(Breakpoint.LG)
        )

        Column(
            modifier = Modifier.fillMaxWidth()
        ) {
            Experience.entries.forEachIndexed { index, experience ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(bottom = if (index == Experience.entries.lastIndex) 0.px else 40.px)
                        .let {
                            if (visibleStates[index]) {
                                it.opacity(1).transform { translateY(0.px) }
                            } else {
                                it.opacity(0).transform { translateY(25.px) }
                            }
                        }
                        .transition(
                            Transition.of("opacity", 0.6.s, TransitionTimingFunction.Ease),
                            Transition.of("transform", 0.6.s, TransitionTimingFunction.Ease)
                        )
                ) {
                    ExperienceCard(experience = experience)
                }
            }
        }
    }

    // Keep the timeline dot aligned to the vertical center of the active
    // experience card. Re-measures after layout settles (cards fade in,
    // images load, etc.). Re-runs whenever `activeExperience` changes or
    // any card's visibility flips, since both affect card heights.
    val visibilityKey = visibleStates.joinToString(",")
    LaunchedEffect(activeExperience, visibilityKey) {
        fun recompute() {
            val section = document.getElementById("experience") as? HTMLElement
            val card = document.getElementById("experience-card-${activeExperience.ordinal}") as? HTMLElement
            if (section != null && card != null) {
                val sectionTop = section.getBoundingClientRect().top
                val cardRect = card.getBoundingClientRect()
                dotTopPx = ((cardRect.top - sectionTop) + (cardRect.height / 2)).toInt()
            }
        }
        // Measure immediately, then again after the next paint (covers
        // post-animation reflows from the fade-in transitions).
        recompute()
        window.requestAnimationFrame {
            recompute()
            window.setTimeout({ recompute() }, 250)
        }
    }

    DisposableEffect(Unit) {
        val onIntersection = { entries: Array<dynamic> ->
            entries.forEach { entry ->
                val el = entry.target as? Element
                if ((entry.isIntersecting as Boolean) && el != null && el.classList.contains("experience-card")) {
                    val id = el.id
                    if (id.startsWith("experience-card-")) {
                        val idx = id.substringAfter("experience-card-").toIntOrNull()
                        if (idx != null) {
                            activeExperience = Experience.entries[idx]
                            visibleStates[idx] = true
                        }
                    }
                }
            }
        }
        val observer = js("new IntersectionObserver(onIntersection, { threshold: 0.35 })")
        document.querySelectorAll(".experience-card").asList().forEach { node ->
            (node as? Element)?.let { observer.observe(it) }
        }

        // Also recompute dot position on resize so it stays aligned when cards reflow.
        val onResize: (org.w3c.dom.events.Event) -> Unit = { _ ->
            val section = document.getElementById("experience") as? HTMLElement
            val card = document.getElementById("experience-card-${activeExperience.ordinal}") as? HTMLElement
            if (section != null && card != null) {
                val sectionTop = section.getBoundingClientRect().top
                val cardRect = card.getBoundingClientRect()
                dotTopPx = ((cardRect.top - sectionTop) + (cardRect.height / 2)).toInt()
            }
        }
        window.addEventListener("resize", onResize)

        onDispose {
            observer.disconnect()
            window.removeEventListener("resize", onResize)
        }
    }
}
